import { Accordion, AccordionDetails, AccordionSummary, Box, SxProps, Theme, Typography } from '@mui/material'
import { Device, ID, Port } from '../types/models'
import { PortControl } from '../classes/ports/PortControl'
import { ArrowForwardIos } from '@mui/icons-material'
import { isDebug } from '../utils/misc'
import { Trans } from 'react-i18next'
import { CustomSwitch } from './atoms/CustomSwitch'
import { StatusLight } from './atoms/StatusLight'
import { PortConfig } from '../types/ports'

export interface PortViewProps {
  device: Device
  portControl: PortControl<Port, PortConfig>
  open: boolean
  onToggle: (state: boolean) => void
  isConfigurable?: boolean
  onUpdate: (params: { name?: string, rule_id?: ID }) => void
  sx?: SxProps<Theme>
}

export function PortView(props: PortViewProps): React.ReactElement {

  const { portControl, sx, open, isConfigurable, onToggle } = props

  const { port } = portControl

  const hasEnableDisable = portControl.hasEnableDisable
  const isTurnedOff = hasEnableDisable && !portControl.getConfig('enabled')
  const infoTop = portControl.infoTop()
  const infoLeft = portControl.infoLeft()
  const infoRight = portControl.infoRight()
  const topLeft = isTurnedOff ? undefined : portControl.configTopLeft()
  const topRight = isTurnedOff ? undefined : portControl.configTopRight()
  const bottomLeft = isTurnedOff ? undefined : portControl.configBottomLeft()
  const bottomRight = isTurnedOff ? undefined : portControl.configBottomRight()

  const onToggleEnable = async () => {
    if (isTurnedOff && !('pending' in port.config)) {
      const defaults: Record<string, unknown> = portControl.getConfigDefaults() as Record<string, unknown>
      const init = Object.assign({}, defaults, port.config)
      for (const key of Object.keys(init)) {
        if (key !== 'enabled' && key !== 'pending' && key !== 'hardware') {
          await portControl.setConfig(key, init[key], false)
        }
      }
    }
    await portControl.setConfig('enabled', !!isTurnedOff)
  }

  return (
    <Accordion disableGutters expanded={open} sx={sx} onChange={(_, expanded) => onToggle(expanded)}>
      <AccordionSummary id={`port-config-${port.id}`} expandIcon={<ArrowForwardIos data-cy={`accordion-${portControl.getName()}`}/>}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'auto 2.5rem',
            alignItems: 'center',
          }}
        >
          <>{`${portControl.getName()}${isDebug('port-numbers') ? ` [#${port.id} ${port.port_number}]` : ''}`}</>
          <StatusLight status={portControl.status}/>
        </Box>
      </AccordionSummary>
      <AccordionDetails data-cy={`box-${portControl.getName()}`}>
        {
          open && <>

            <Typography sx={{ ml: '0.5rem' }}>{portControl.getDescription()}</Typography>

          {
            hasEnableDisable && isConfigurable &&
              <Box sx={{ m: '0.5rem' }}>
                <Trans>Enabled</Trans>
                <CustomSwitch data-cy="switch-Enabled" onClick={onToggleEnable} checked={!isTurnedOff} sx={{ ml: '1rem' }}/>
              </Box>
          }
          <Box sx={{ m: '0.5rem', display: 'grid', gridTemplateColumns: '50% 50%' }}>

            { /* Info panel top section */
              infoTop &&
                <Box sx={{ width: '200%', mb: 2 }}>
                  {infoTop}
                </Box>
            }
            {
              infoTop &&
                <Box sx={{ width: '0%' }} />
            }

            { /* Info panel left section */
              (infoLeft || infoRight) &&
                <Box sx={{ mb: 2 }}>
                  {infoLeft}
                </Box>
            }

            { /* Info panel right section */
              (infoLeft || infoRight) &&
                <Box sx={{ borderLeft: '1px solid #737373', pl: '1rem' }}>
                  {infoRight}
                </Box>
            }

            { /* Software configuration left section */
              (topLeft || topRight) &&
                <Box sx={{ mb: 2 }}>
                  {topLeft}
                </Box>
            }

            { /* Software configuration right section */
              (topLeft || topRight) &&
                <Box sx={{ borderLeft: '1px solid #737373', pl: '1rem' }}>
                  {topRight}
                </Box>
            }

            { /* Hardware configuration left section */
              (bottomLeft || bottomRight) &&
                <Box sx={{ mb: 2 }}>
                  {bottomLeft}
                </Box>
            }

            { /* Hardware configuration right section */
              (bottomLeft || bottomRight) &&
                <Box sx={{ borderLeft: '1px solid #737373', pl: '1rem' }}>
                  {bottomRight}
                </Box>
            }

            { /* Debug view */ }
            { isDebug('port-json') && <Box><hr/></Box> }
            { isDebug('port-json') && <Box><hr/></Box> }
            { isDebug('port-json') && <Box>
                <Typography>Config</Typography>
                <pre style={{ fontSize: '80%', whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
                  {JSON.stringify(port.config, null, 2)}
                </pre>
              </Box>
            }
            { isDebug('port-json') && <Box sx={{ borderLeft: '1px solid #737373', pl: '1rem', minHeight: '4rem' }}>
                <Typography>Values</Typography>
                <pre style={{ fontSize: '80%' }}>
                  {JSON.stringify(port.values, null, 2)}
                </pre>
                <Typography>Flags</Typography>
                {JSON.stringify(port.flags)}
              </Box>
            }

          </Box>
        </>
        }
      </AccordionDetails>
    </Accordion>
  )
}
